/**
 * PipelineBoard — generic "By Status" kanban view (FieldPulse style).
 * Columns grouped by canonical status, each header shows the count and the
 * summed amount for that column. The list/classic view stays available; this
 * is purely a presentation grouping (no DB status values are changed).
 */
export default function PipelineBoard({ columns, items, getStatus, renderCard, sumOf, emptyLabel = 'No records' }) {
  const grouped = {};
  columns.forEach(col => { grouped[col.key] = []; });
  (items || []).forEach(item => {
    const key = getStatus(item);
    if (grouped[key]) grouped[key].push(item);
    else if (grouped.__other) grouped.__other.push(item);
  });

  const money = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  return (
    <div className="overflow-x-auto pb-3 -mx-1 px-1" data-testid="pipeline-board">
      <div className="flex gap-3 min-w-min">
        {columns.map(col => {
          const colItems = grouped[col.key] || [];
          const colSum = sumOf ? colItems.reduce((s, it) => s + (sumOf(it) || 0), 0) : 0;
          return (
            <div key={col.key} data-testid={`pipeline-col-${col.key}`} className="w-[280px] flex-shrink-0 flex flex-col">
              <div className={`rounded-t-xl px-3 py-2.5 border-b-2 ${col.headerClass || 'bg-gray-50 border-gray-300'}`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-wide text-gray-700 flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${col.dotClass || 'bg-gray-400'}`} />
                    {col.label}
                  </span>
                  <span className="text-[11px] font-semibold text-gray-500 bg-white/70 rounded-full px-2 py-0.5" data-testid={`pipeline-count-${col.key}`}>
                    {colItems.length}
                  </span>
                </div>
                {sumOf && (
                  <p className="text-sm font-bold text-gray-900 mt-1" data-testid={`pipeline-sum-${col.key}`}>{money(colSum)}</p>
                )}
              </div>
              <div className="flex-1 bg-gray-50/60 rounded-b-xl p-2 space-y-2 min-h-[120px] max-h-[68vh] overflow-y-auto">
                {colItems.length === 0 ? (
                  <p className="text-[11px] text-gray-300 italic text-center py-6">{emptyLabel}</p>
                ) : (
                  colItems.map(item => renderCard(item))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
